import type { UseChatHelpers } from "@ai-sdk/react";
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import equal from "fast-deep-equal";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Fragment, memo, useCallback, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { ArtifactUpdateBadge } from "@/components/artifacts/artifact-update-badge";
import { DocumentPreview } from "@/components/artifacts/document-preview";
import { Streamdown } from "streamdown";
import { useTextToSpeech } from "@/hooks/use-text-to-speech";
import { useToolVisibility } from "@/hooks/use-tool-visibility";
import { isEmbedSurface } from "@/lib/embed-locale";
import type { MessageArtifactRenderPlan } from "@/lib/artifacts/render-modes";
import { getChatIdFromPathname } from "@/lib/chat-icon";
import {
  parseFormSubmission,
  summarizeSubmission,
} from "@/lib/json-render/form-submission";
import { cn, sanitizeText } from "@/lib/utils";
import { ChatMessage, type ChatSurface } from "@/types/chat-message";
import { ARTIFACT_TOOL_NAMES } from "@taqat/core/types";
import type { CompactionEvent } from "@taqat/ai";
import { buildActivitySegments, isContentToolPart, isPlanToolPart } from "./activity-segments";
import { isToolPartType } from "./tool-call-message/tool-part";
import { MessageActivityGroup, MessageActivityStep } from "./message-activity-group";
import { CodingFileSummary } from "./coding-file-summary";
import { CodingTurnSummary } from "./coding-turn-summary";
import {
  planCodingTurn,
  summarizeChangedFiles,
  turnDuration,
  type CodingTimelineSegment,
  type CodingTurnMetadata,
} from "./coding-turn";
import { CompactionDivider } from "./compaction-divider";
import { PreviewAttachment } from "../file/preview-attachment";
import { ChartDisplay } from "../tool-views/chart-display";
import type { AgentCallStreamStats } from "./agent-call-stream-state";
import { ErrorMessage } from "./error-message";
import { splitChartMarkdown } from "./chart-markdown";
import { normalizeSplitOrderedListMarkers } from "./markdown-normalization";
import { MessageActions } from "./message-actions";
import { MessageEditor } from "./message-editor";
import { MessageReasoning } from "./message-reasoning";
import {
  CodingCommandPart,
  CodingFileChangePart,
  CodingPlanPart,
  CodingToolCallPart,
} from "./coding-parts";
import { CodingToolRun } from "./coding-tool-run";
import { isReasoningPartStreaming } from "./reasoning-part-state";
import ToolCallMessage from "./tool-call-message";
import { DetailedAttachmentList } from "./tool-call-message/components";

/**
 * How many deliverable chips render inline before the rest are summarised.
 * Four keeps the reply visible on a laptop viewport; beyond that the list
 * starts pushing the agent's answer off screen.
 */
const MAX_INLINE_CHIPS = 4;

type CitationSource = {
  key: string;
  label: string;
  url?: string;
  domain?: string;
};

const FOOTNOTE_DEFINITION_PATTERN = /^\[\^([^\]]+)\]:\s*(.+)$/gm;
const DASH_SPLIT_PATTERN = /\s+[—–-]\s+/;
const COLLAPSED_SOURCE_COUNT = 4;

/**
 * Extracts GFM footnote definitions into source metadata for the chat UI.
 *
 * Streamdown still receives the original markdown so inline citation markers
 * render correctly; only the duplicated footnote block is replaced visually.
 */
function extractCitationSources(markdown: string): CitationSource[] {
  const sources: CitationSource[] = [];
  for (const match of markdown.matchAll(FOOTNOTE_DEFINITION_PATTERN)) {
    const key = match[1]?.trim();
    const body = match[2]?.trim();
    if (!key || !body) continue;
    sources.push(toCitationSource(key, body));
  }
  return sources;
}

/**
 * Converts a raw footnote body into a compact source card label and URL.
 *
 * Supports both markdown links and the plain "Title — domain/path" format
 * produced by research answers.
 */
function toCitationSource(key: string, body: string): CitationSource {
  const markdownLink = body.match(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/);
  if (markdownLink) {
    const url = markdownLink[2];
    return {
      key,
      label: markdownLink[1].trim(),
      url,
      domain: getSourceDomain(url),
    };
  }

  const parts = body.split(DASH_SPLIT_PATTERN);
  const maybeUrl = parts.length > 1 ? parts[parts.length - 1]?.trim() : undefined;
  const url = maybeUrl ? normalizeSourceUrl(stripMarkdown(maybeUrl)) : undefined;
  const title = parts.length > 1 ? parts.slice(0, -1).join(" - ").trim() : body;

  return {
    key,
    label: stripMarkdown(title || body),
    url,
    domain: url ? getSourceDomain(url) : undefined,
  };
}

/** Removes markdown-only syntax that should not appear inside source cards. */
function stripMarkdown(value: string): string {
  return value
    .replace(/\s+\[?↩.*$/u, "")
    .replace(/[`*_]+/g, "")
    .trim();
}

/** Normalizes source text into a browser-openable URL when possible. */
function normalizeSourceUrl(value: string): string | undefined {
  const cleaned = value.trim().replace(/[),.;]+$/g, "");
  if (!cleaned) return undefined;
  const withProtocol = /^https?:\/\//i.test(cleaned) ? cleaned : `https://${cleaned}`;
  try {
    return new URL(withProtocol).toString();
  } catch {
    return undefined;
  }
}

/** Returns a short display domain for source cards. */
function getSourceDomain(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}


/**
 * Renders streamed chat markdown with chat-specific layout safeguards.
 *
 * Research footnotes are rendered as a compact source strip, matching the
 * interaction pattern users expect from web-enabled chat answers.
 */
function ChatMarkdown({ children }: { children: string }) {
  const normalizedMarkdown = useMemo(
    () => normalizeSplitOrderedListMarkers(children),
    [children],
  );
  const sources = useMemo(
    () => extractCitationSources(normalizedMarkdown),
    [normalizedMarkdown],
  );
  const segments = useMemo(
    () => splitChartMarkdown(normalizedMarkdown),
    [normalizedMarkdown],
  );

  return (
    <div
      className={cn(
        "max-w-full wrap-anywhere",
        "[&>div>*:first-child]:mt-0 [&>div>*:last-child]:mb-0",
        "[&_li>p:first-child]:m-0 [&_li>p:first-child]:inline",
        "[&_section[data-footnotes]]:hidden",
        "[&_sup[data-footnote-ref]]:text-[10px]",
        "[&_sup[data-footnote-ref]]:leading-none"
      )}
    >
      {segments.map((segment, index) => {
        if (segment.type === "chart") {
          return (
            <div
              key={`chart-${index}`}
              className="not-prose my-4"
              aria-label={segment.alt || segment.config.title}
            >
              <ChartDisplay config={segment.config} className="w-full" />
            </div>
          );
        }

        return segment.text ? (
          <Streamdown key={`markdown-${index}`}>{segment.text}</Streamdown>
        ) : null;
      })}
      {sources.length > 0 ? <CitationSources sources={sources} /> : null}
    </div>
  );
}

/**
 * Compact source surface for research answers.
 *
 * Defaults to a short row so citations remain available without turning the
 * bottom of the conversation into a bibliography.
 */
function CitationSources({ sources }: { sources: CitationSource[] }) {
  const [expanded, setExpanded] = useState(false);
  const visibleSources = expanded ? sources : sources.slice(0, COLLAPSED_SOURCE_COUNT);
  const remainingCount = sources.length - COLLAPSED_SOURCE_COUNT;

  return (
    <div className="not-prose mt-4 border-t border-border/60 pt-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-xs font-medium text-muted-foreground">
          Sources <span className="font-normal">({sources.length})</span>
        </div>
        {remainingCount > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {expanded ? "Show less" : `Show ${remainingCount} more`}
            {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visibleSources.map((source) => (
          <SourceCard key={source.key} source={source} />
        ))}
      </div>
    </div>
  );
}

/** Renders one compact, clickable source with a stable citation number. */
function SourceCard({ source }: { source: CitationSource }) {
  const content = (
    <>
      <span className="flex size-5 shrink-0 items-center justify-center rounded bg-muted text-[11px] font-semibold text-muted-foreground">
        {source.key}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-foreground">{source.label}</span>
        {source.domain ? (
          <span className="block truncate text-[11px] text-muted-foreground">{source.domain}</span>
        ) : null}
      </span>
      {source.url ? <ExternalLink className="size-3 shrink-0 text-muted-foreground" /> : null}
    </>
  );

  const className =
    "flex min-w-0 items-center gap-2 rounded-md border border-border/70 bg-background/70 px-2.5 py-2 text-left transition-colors hover:bg-muted/60";

  if (!source.url) {
    return <div className={className}>{content}</div>;
  }

  return (
    <a href={source.url} target="_blank" rel="noopener noreferrer" className={className}>
      {content}
    </a>
  );
}


/**
 * PurePreviewMessage component renders chat messages with support for text, files, reasoning, and tool calls.
 *
 * This component handles the complete rendering of a chat message including:
 * - Text content with markdown support
 * - File attachments with preview functionality
 * - Tool call results with dynamic UI
 * - Reasoning sections for AI assistant messages
 * - Error states with retry functionality
 * - Interactive actions (edit, regenerate) on hover
 * - Edit mode for message modification
 *
 * The component supports both user and assistant messages with different visual treatments:
 * - User messages are styled with primary background and right-aligned
 * - Assistant messages have a clean layout with hover actions
 * - Error messages display with proper error UI and retry options
 *
 * @param sandboxId - Optional sandbox ID for tool execution context
 * @param message - The chat message object containing content and metadata
 * @param isLoading - Whether the message is currently being processed
 * @param setMessages - Function to update the messages array
 * @param regenerate - Function to regenerate the assistant's response
 * @param isReadonly - Whether the message is in read-only mode (no actions)
 * @param requiresScrollPadding - Whether extra padding is needed for scrolling
 * @returns JSX.Element representing the rendered message
 */
const PurePreviewMessage = ({
  sandboxId,
  surface = "assistant",
  message,
  isLoading,
  setMessages,
  regenerate,
  isReadonly,
  requiresScrollPadding: _requiresScrollPadding,
  isLastMessage = false,
  isCompact = false,
  textToSpeechEnabled = false,
  voiceSettings,
  nextUserMessageText,
  agentStreams,
  agentStreamStats,
  agentFinalTexts,
  artifactRenderPlan,
  autoChipFiles,
  isLastAssistantOfTurn = true,
  compactions,
}: {
  sandboxId?: string;
  /**
   * Which product surface is rendering. Coding draws a turn as a column of
   * build steps — the newest visible, the rest folded — rather than as an
   * answer with its tool work tucked behind a summary.
   */
  surface?: ChatSurface;
  message: ChatMessage;
  isLoading: boolean;
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  regenerate: UseChatHelpers<ChatMessage>["regenerate"];
  isReadonly: boolean;
  requiresScrollPadding: boolean;
  isLastMessage?: boolean;
  isCompact?: boolean;
  textToSpeechEnabled?: boolean;
  voiceSettings?: { voice?: string; speed?: number; pitch?: number };
  nextUserMessageText?: string;
  agentStreams?: Record<string, string>;
  agentStreamStats?: Record<string, AgentCallStreamStats>;
  agentFinalTexts?: Record<string, string>;
  /** Per-message render plan from `computeArtifactRenderModes` (optional). */
  artifactRenderPlan?: MessageArtifactRenderPlan;
  /**
   * Optional list of file paths to render as chips at the bottom of this
   * message. Set by `computeChipRenderPlans` only on the *last* assistant
   * message of a turn, so the chip surface lands under the agent's final
   * text reply rather than above earlier tool-call messages.
   */
  autoChipFiles?: string[];
  /**
   * Whether this assistant message is the final one in its turn (i.e. the
   * next message is a real user prompt, or this is the last message overall).
   * Intermediate assistant messages — reasoning + tool-call segments produced
   * by multi-step agents — are conceptually part of one reply and shouldn't
   * carry their own copy/regenerate footer. Defaults to true so legacy
   * callers without per-turn metadata keep their existing footer behaviour.
   */
  isLastAssistantOfTurn?: boolean;
  /**
   * Compaction events that fired during this message's turn, to render inline
   * right after the tool card they occurred on (matched by `afterToolCallId`).
   */
  compactions?: CompactionEvent[];
}) => {

  const [mode, setMode] = useState<"view" | "edit">("view");
  const fileParts = message.parts.filter((part) => part.type === "file");

  /**
   * Index compaction events by the tool-call id they fired after, so the
   * per-part render can drop the divider inline in O(1). On reload this places
   * the divider at the same spot it appeared during the live stream instead of
   * collapsing to the end of the message.
   */
  const inlineCompactionsByToolCallId = useMemo(() => {
    const map = new Map<string, CompactionEvent[]>();
    for (const event of compactions ?? []) {
      const anchor = event.afterToolCallId;
      if (!anchor) continue;
      const existing = map.get(anchor);
      if (existing) existing.push(event);
      else map.set(anchor, [event]);
    }
    return map;
  }, [compactions]);

  /**
   * TTS hook - lifted here so speech continues when MessageActions unmounts on mouse out
   */
  const { speak, stop, isSpeaking, isSupported: isTTSSupported } = useTextToSpeech(voiceSettings);

  /**
   * Workspace-level tool-visibility flags (issue #1332). When tool calls are
   * hidden, only true tool *activity* is suppressed — content-bearing tools
   * (the agent's question/answer, rendered views) stay visible.
   */
  const { showToolCalls: showToolCallsRaw, resolved: toolVisibilityResolved } = useToolVisibility();

  /**
   * Bubble/embed chat is a clean, donor-facing surface: hide the agent's
   * "thinking" (reasoning trace) and tool-call activity entirely. Content-
   * bearing tools (rendered forms/answers) still show via isContentToolPart.
   * Scoped to the embed iframe only — the main app is unaffected.
   */
  const onEmbed = isEmbedSurface();
  const showToolCalls = showToolCallsRaw && !onEmbed;

  /**
   * Teammate Code. A turn there is a sequence of build steps rather than an
   * answer with tool work behind it, so its steps get their own row and its
   * runs show the newest step instead of hiding all of them behind a count.
   */
  const isCodingSurface = surface === "coding";

  /**
   * Get message text content for TTS
   */
  const getMessageContent = useCallback(() => {
    if (Array.isArray(message.parts) && message.parts.length > 0) {
      return message.parts
        .filter((part: any) => part.type === "text")
        .map((part: any) => part.text || "")
        .filter((text: string) => text.trim().length > 0)
        .join("\n\n");
    }
    return "";
  }, [message.parts]);

  /**
   * Server may be marked the error in the metadata. So treat
   * this message as an error message.
   */
  const hasError = message?.metadata?.error === true;

  /**
   * Is user message or assistant message
   */
  const isUserMessage = useMemo(() => {
    return message.role === "user";
  }, [message.role]);

  /**
   * A form-submission receipt is a full-width card, not a chat bubble.
   *
   * User messages are shrink-wrapped (`w-fit`) and capped at `max-w-2xl` so a
   * short reply doesn't stretch across the transcript. The receipt is a table of
   * label/value rows, so that same wrapping squeezes it to its longest row and
   * the values have nothing to align against — it needs the full column width.
   */
  const isFormSubmissionMessage = useMemo(() => {
    if (message.role !== "user") return false;
    return message.parts.some(
      (part) =>
        part.type === "text" &&
        parseFormSubmission((part as { text?: string }).text) !== null
    );
  }, [message.role, message.parts]);

  /**
   * Whether the message has meaningful text content worth showing actions for.
   * Prevents copy/regenerate from appearing on empty assistant messages.
   */
  const hasTextContent = useMemo(() => {
    return message.parts.some(
      (part) =>
        (part.type === "text" || (part.type as string) === "text-delta") &&
        ((part as { text: string }).text || "").trim().length > 0
    );
  }, [message.parts]);

  /**
   * Can show the action or not. Only the *last* assistant message of a turn
   * with text content shows actions — intermediate steps in a multi-step
   * agent reply are visually one logical message, so attaching copy /
   * regenerate to each segment would mislead the user into treating them as
   * separate replies. User messages and empty/tool-only assistant messages
   * never show actions.
   */
  const canShowActions = useMemo(() => {
    return (
      !isUserMessage &&
      !isReadonly &&
      !hasError &&
      !isLoading &&
      hasTextContent &&
      isLastAssistantOfTurn
    );
  }, [
    isUserMessage,
    isReadonly,
    hasError,
    isLoading,
    hasTextContent,
    isLastAssistantOfTurn,
  ]);

  /**
   * Check if the message contains a 'complete' tool call with text content.
   * When present, text parts should be skipped to avoid duplication since
   * the CompleteRenderer already displays the text from the tool's input.
   */
  const hasCompleteToolWithText = useMemo(() => {
    return message.parts.some((part) => {
      if (part.type.startsWith('tool-') && part.type.split('-')[1] === 'complete') {
        const input = (part as any).input;
        return input?.text || input?.content;
      }
      return false;
    });
  }, [message.parts]);

  /**
   * Whether to render the auto-chip surface on this message. Suppressed while
   * this message is still streaming so chips appear only after the final text
   * reply has settled.
   */
  const navigate = useNavigate();

  const shouldRenderAutoChips =
    !isUserMessage && !isLoading && (autoChipFiles?.length ?? 0) > 0;

  /**
   * Chips are bounded, not unbounded.
   *
   * Even after filtering to deliverables a task can produce a dozen documents,
   * and an inline list that long pushes the agent's actual reply off screen —
   * the same complaint filed against Copilot Chat's "N files changed" panel.
   * Show a few and account for the rest in one line; everything remains in the
   * Library, which lists workspace files per conversation.
   */
  const visibleChipFiles = useMemo(
    () => (autoChipFiles ?? []).slice(0, MAX_INLINE_CHIPS),
    [autoChipFiles],
  );
  const hiddenChipCount = (autoChipFiles?.length ?? 0) - visibleChipFiles.length;
  /**
   * The Library tab only exists on agent chat surfaces. Elsewhere the overflow
   * is stated without a control rather than pointing somewhere that doesn't
   * exist.
   */
  const canOpenLibraryTab =
    typeof window !== "undefined" && window.location.pathname.includes("/agents/");

  /**
   * Switch to the Library tab in place.
   *
   * Routed, not an `<a href>` — an anchor reloads the document, throwing away
   * the conversation the user is reading just to change tab. Navigating
   * against `window.location.pathname` rather than React Router's location is
   * deliberate and matches `AgentChatTabs.handleTabChange`: after a new chat
   * the page rewrites /chat/new to /chat/<id> with `history.replaceState`,
   * which React Router never observes, so building from its stale location
   * would send the user back to /chat/new.
   */
  const openLibraryTab = useCallback(() => {
    /*
      Carry the intent in the URL: the user clicked "the rest of THIS turn's
      files", so the Library should open already narrowed to workspace files
      from this conversation instead of the agent-wide default. Params, not
      shared state, so the deep link survives a reload and — because a plain
      tab click never sets them — ordinary tab switching is unaffected.
    */
    const params = new URLSearchParams({ tab: "library", libSource: "workspace" });
    const chatId = getChatIdFromPathname();
    if (chatId) params.set("libChat", chatId);
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  }, [navigate]);

  /**
   * Handle rendering the message parts. Iterate over each part and render it accordingly.
   * @returns JSX.Element
   */
  const renderOnePart = (part: ChatMessage["parts"][number], index: number): ReactNode => {
      const key = `message-${message.id}-part-${index}`;
      const { type } = part;

      /**
       * Render the reasoning part of the message.
       * Only shows for the assistant messages with reasoning enabled.
       * Handle both "reasoning" (final) and "reasoning-delta" (streaming) types.
       */
      if ((type as string) === "reasoning" || (type as string) === "reasoning-delta") {
        // Bubble/embed: never surface the agent's thinking trace.
        if (onEmbed) return null;
        const reasoningText = (part as any).text || (part as any).delta || (part as any).reasoning || "";
        const isReasoningStreaming = isReasoningPartStreaming(part as { type: string; state?: string });

        // Skip rendering empty or redacted-only reasoning blocks (reduces UI clutter)
        const trimmedReasoning = reasoningText.trim();
        if (!isReasoningStreaming && (trimmedReasoning.length === 0 || trimmedReasoning === "[REDACTED]")) {
          return null;
        }

        return (
          <MessageReasoning
            key={key}
            isLoading={isReasoningStreaming}
            reasoning={reasoningText}
            reasoningTokens={(message.metadata as { reasoningTokens?: number } | undefined)?.reasoningTokens}
          />
        );
      }

      /**
       * Render the Teammate Code data parts (spec 083 §6).
       *
       * Coding output has no native AI SDK chunk type, so file changes, shell
       * commands and plans arrive as typed `data-*` parts. Drawing them here
       * keeps the coding surface on this one renderer instead of a second
       * transcript UI.
       */
      if ((type as string) === "data-file-change") {
        return <CodingFileChangePart key={key} data={(part as any).data ?? {}} />;
      }
      if ((type as string) === "data-command") {
        return <CodingCommandPart key={key} data={(part as any).data ?? {}} />;
      }
      if ((type as string) === "data-plan") {
        return <CodingPlanPart key={key} data={(part as any).data ?? {}} />;
      }

      /*
        Tool calls on the coding surface get their own row rather than going
        through `ToolCallMessage`. That component renders the platform's whole
        tool vocabulary — artifacts, agent calls, browser screenshots, rendered
        forms — none of which a sandbox emits, and its row is shaped for a chat
        answer rather than for a column of build steps.
      */
      if (isCodingSurface && isToolPartType(type)) {
        if (!toolVisibilityResolved || !showToolCalls) return null;
        return (
          <CodingToolCallPart
            key={key}
            part={part as { toolName?: string; state?: string }}
            isMessageStreaming={isLoading}
          />
        );
      }

      /**
       * Render data-chart parts from chart generation tool
       * These are streamed data parts containing chart configurations
       */
      if ((type as string) === "data-chart") {
        return (
          <div key={key} className={cn("my-4", isCompact ? "mx-2" : "mx-0")}>
            <ChartDisplay
              config={(part as any).data}
              className="w-full"
            />
          </div>
        );
      }

      /**
       * Extract and render chart data from tool outputs for persistence after refresh
       * Check if this is a tool call that contains chart data in its output
       */
      if (isToolPartType(type) &&
        (part as any).output?.chartData &&
        !message.parts.some(p => (p.type as string) === "data-chart")) {
        const chartData = (part as any).output.chartData;
        return (
          <div key={key}>
            {/* Render the chart from persisted data */}
            <div className={cn("my-4", isCompact ? "mx-2" : "mx-0")}>
              <ChartDisplay
                config={chartData.data}
                className="w-full"
              />
            </div>
            {/* Still render the tool call for debugging and completeness,
                unless the workspace hides tool calls or settings haven't
                resolved yet (issue #1332). */}
            {toolVisibilityResolved && showToolCalls && (
              <ToolCallMessage
                part={part as ToolUIPart | DynamicToolUIPart}
                sandboxId={sandboxId}
                isLastMessage={isLastMessage}
                nextUserMessageText={nextUserMessageText}
                agentStreams={agentStreams}
                agentStreamStats={agentStreamStats}
                agentFinalTexts={agentFinalTexts}
              />
            )}
          </div>
        );
      }

      /**
       * Artifact tool parts get bespoke rendering: full preview card for the
       * latest create, compact badge for updates and superseded creates,
       * hidden for back-to-back model retries within the same message. The
       * mode is supplied by `artifactRenderPlan` from `messages.tsx`.
       */
      if (
        type === `tool-${ARTIFACT_TOOL_NAMES.CREATE}` ||
        type === `tool-${ARTIFACT_TOOL_NAMES.UPDATE}` ||
        type === `tool-${ARTIFACT_TOOL_NAMES.GENERATE_IMAGE}`
      ) {
        const toolName = type.slice("tool-".length);
        const planned = artifactRenderPlan?.get(index);
        const mode =
          planned ?? (toolName === ARTIFACT_TOOL_NAMES.UPDATE ? "badge" : "preview");

        if (mode === "hidden") return null;
        if (mode === "badge") {
          return <ArtifactUpdateBadge key={key} part={part as ToolUIPart} />;
        }
        return <DocumentPreview key={key} part={part as ToolUIPart} />;
      }

      /**
       * `update_plan` draws nothing here — the plan panel above the composer is
       * its UI, so a chip would be the same information twice. Skipped rather
       * than styled because the runtime asks for the list at the start of every
       * turn, so this fires even on a greeting that plans nothing.
       */
      if (isPlanToolPart(part as { type: string; toolName?: string })) return null;

      /**
       * All tool calls. This will have the tool input and output. This is the most complex
       * part of the rendering this message. Each tool will have different type of input and output
       * and based on that we need to show different UI.
       *
       * Covers `dynamic-tool` as well as `tool-<name>`: every Teammate Code tool
       * call is dynamic (the sandbox declares its own tools), as are MCP and
       * Composio ones. Testing the `tool-` prefix here meant those parts matched
       * no branch at all and the transcript drew nothing for them.
       */
      if (isToolPartType(type)) {
        /**
         * Hide tool *activity* when the workspace disables tool calls — or
         * until the visibility settings resolve, so a workspace that hides
         * tools never flashes them on first paint. Content-bearing tools
         * (question/answer/rendered views) stay visible (issue #1332).
         */
        if ((!toolVisibilityResolved || !showToolCalls) && !isContentToolPart(part)) {
          return null;
        }
        return (
          <ToolCallMessage
            key={key}
            part={part as ToolUIPart | DynamicToolUIPart}
            sandboxId={sandboxId}
            isLastMessage={isLastMessage}
            nextUserMessageText={nextUserMessageText}
            isMessageStreaming={isLoading}
            agentStreams={agentStreams}
            agentStreamStats={agentStreamStats}
            agentFinalTexts={agentFinalTexts}
          />
        );
      }

      /**
       * Render normal text part and text-delta during streaming.
       * text-delta parts are sent during streaming, text parts are the final result.
       * Skip text parts when a 'complete' tool call exists with text to avoid duplication.
       */
      if ((type === "text" || (type as string) === "text-delta") && hasCompleteToolWithText && !isLoading) {
        return null;
      }
      if (type === "text" || (type as string) === "text-delta") {
        // Skip empty / whitespace-only text parts (e.g. stray "\n\n" segments
        // emitted between tool calls). Rendering them produces a blank wrapper
        // that still carries gap/padding and shows up as an empty band in the
        // message. Mirrors the same guard used for reasoning blocks above.
        const textValue = (part as { text: string }).text || "";
        if (!isLoading && textValue.trim().length === 0) {
          return null;
        }
        /**
         * A structured form submission (from `render_ui`'s submitFormData
         * action) arrives as a fenced envelope on a user turn. Show the user a
         * compact "submitted" card instead of the raw JSON the model consumes.
         */
        const formSubmission =
          message.role === "user" ? parseFormSubmission(textValue) : null;
        if (formSubmission && mode === "view") {
          const fields = summarizeSubmission(formSubmission.values);
          return (
            <div
              key={key}
              className={cn(
                "flex flex-row items-start min-w-0 w-full",
                isCompact ? "gap-1" : "gap-2",
              )}
            >
              {/*
                Reads as a receipt rather than a chat bubble: a full-width card
                with a green edge for "recorded", labels left and values aligned
                to the end so the figures line up down the card. Logical
                properties throughout (`border-s`, `text-end`) so it mirrors
                correctly in Arabic.
              */}
              <div
                data-testid="message-content"
                dir="auto"
                className="w-full min-w-0 overflow-hidden rounded-xl border border-border border-s-4 border-s-primary bg-card px-4 py-3"
              >
                <p className="text-sm font-semibold text-primary">
                  {formSubmission.summary || "Request recorded"}
                </p>
                {fields.length > 0 && (
                  <dl className="mt-2.5 grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
                    {fields.map((f) => (
                      <Fragment key={f.key}>
                        <dt className="capitalize text-muted-foreground">{f.key}</dt>
                        <dd className="text-end wrap-anywhere text-foreground">
                          {/*
                            A `status` field is the outcome of the request, not
                            another detail — it gets a pill so it reads as state.
                          */}
                          {f.key === "status" ? (
                            <span className="inline-block rounded-md border border-warning/20 bg-warning/10 px-2 py-0.5 font-medium text-warning">
                              {f.value}
                            </span>
                          ) : (
                            f.value
                          )}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                )}
              </div>
            </div>
          );
        }
        if (mode === "view") {
          return (
            <div
              key={key}
              className={cn(
                "flex flex-row items-start min-w-0 w-full",
                isCompact ? "gap-1" : "gap-2",
                message.role === "user" && "justify-end",
              )}
            >
              <div
                data-testid="message-content"
                /* Per-message direction: each message picks its own direction
                   from its first strong character, so an English reply inside
                   an Arabic (RTL) session renders LTR and vice versa. Inert
                   for the all-LTR case. (spec 075 — Phase 3) */
                dir="auto"
                className={cn("flex flex-col min-w-0 max-w-full wrap-anywhere",
                  isCompact ? "gap-2" : "gap-4",
                  message.role === "user" ? "w-fit" : "w-full",
                  {
                    "rounded-xl bg-muted text-foreground":
                      message.role === "user",
                    "px-2 py-1.5": message.role === "user" && isCompact,
                    "px-3 py-2": message.role === "user" && !isCompact,
                  }
                )}
              >
                {/*
                  Streamdown (used by the reasoning panel) gives code blocks
                  the same syntax-highlighted, language-tagged, copy-able
                  treatment as image 19 — switching the chat-text path off
                  the custom Markdown component keeps the two surfaces
                  visually consistent. User messages render fine through
                  Streamdown too (plain text passes through unchanged).
                */}
                <ChatMarkdown>
                  {sanitizeText((part as { text: string }).text)}
                </ChatMarkdown>
              </div>
            </div>
          );
        } else {
          return (
            <div key={key} className="flex flex-row items-start gap-2">
              <MessageEditor
                key={message.id}
                message={message}
                setMode={setMode}
                setMessages={setMessages}
                regenerate={regenerate}
              />
            </div>
          );
        }
      }
  };

  /** Compaction dividers anchored to a part's tool call, if any. */
  const dividersForPart = (part: ChatMessage["parts"][number]): CompactionEvent[] => {
    const toolCallId = (part as { toolCallId?: string }).toolCallId;
    return (toolCallId ? inlineCompactionsByToolCallId.get(toolCallId) : undefined) ?? [];
  };

  const renderDividers = (events: CompactionEvent[]): ReactNode =>
    events.map((event) => (
      <CompactionDivider
        key={event.id}
        messageCount={event.messageCount}
        createdAt={event.createdAt}
        summary={event.summary}
      />
    ));

  /**
   * Runs of consecutive agent activity (thinking + tool calls) collapse into a
   * single expandable group so the answer isn't buried under machine chatter
   * (issue #1458). Everything else renders exactly as before.
   */
  const activitySegments = useMemo(
    () =>
      buildActivitySegments(message.parts, {
        isMessageStreaming: isLoading,
        showToolActivity: toolVisibilityResolved && showToolCalls,
      }),
    [message.parts, isLoading, toolVisibilityResolved, showToolCalls],
  );

  /**
   * Coding lays a turn out on its own terms: live, a column of steps with the
   * newest visible; finished, one `14 steps · 3m 12s ✓` row with the answer
   * under it. See `coding-turn.ts` for why the fold is whole-turn rather than
   * per-run.
   */
  const codingMetadata = message.metadata as CodingTurnMetadata | undefined;

  /*
    A turn is live while its stream is running — and also when the row still
    says `streaming` with no stream attached, which is what a reload mid-turn
    looks like (spec 083 §8.3). Collapsing that one would stamp a finished tick
    on a turn that never ended.

    `interrupted` reads the same way: the sandbox lost its event socket and is
    usually still working, so the turn resumes when it reconnects. The reconcile
    cron is what turns that into `failed` when it does not.
  */
  const isCodingTurnLive =
    isLoading ||
    codingMetadata?.status === "streaming" ||
    codingMetadata?.status === "interrupted";

  const codingSegments = useMemo(
    () =>
      isCodingSurface
        ? planCodingTurn(message.parts, {
            isMessageStreaming: isCodingTurnLive,
            showToolActivity: toolVisibilityResolved && showToolCalls,
          })
        : [],
    [isCodingSurface, message.parts, isCodingTurnLive, toolVisibilityResolved, showToolCalls],
  );

  /**
   * What the turn changed, shown as a card under the answer once it has ended.
   * Mid-turn the counts are still moving and the file rows in the live column
   * already say what is happening.
   */
  const codingChanges = useMemo(
    () =>
      isCodingSurface && !isCodingTurnLive ? summarizeChangedFiles(message.parts) : undefined,
    [isCodingSurface, isCodingTurnLive, message.parts],
  );

  /**
   * Draws one segment of the coding timeline.
   *
   * Used for the live timeline and, unchanged, for the contents of a collapsed
   * turn — which is what makes an expanded turn identical to the same turn while
   * it streamed, down to the inner "+N previous tool calls" folds. Rendering the
   * fold through a second, flatter path is exactly the drift this avoids.
   */
  const renderCodingTimelineSegment = (segment: CodingTimelineSegment): ReactNode => {
    if (segment.kind === "part") {
      const part = message.parts[segment.index];
      const element = renderOnePart(part, segment.index);
      const dividers = dividersForPart(part);
      if (dividers.length === 0) return element;
      return (
        <Fragment key={`message-${message.id}-part-${segment.index}-with-compaction`}>
          {element}
          {renderDividers(dividers)}
        </Fragment>
      );
    }

    // Compaction dividers stay on the timeline rather than being collapsed away
    // with the steps they were anchored to.
    const dividers = segment.items.flatMap((item) =>
      dividersForPart(message.parts[item.index]),
    );

    return (
      <Fragment key={`message-${message.id}-${segment.id}`}>
        <CodingToolRun
          items={segment.items.map((item) => (
            <Fragment key={`message-${message.id}-part-${item.index}-step`}>
              {renderOnePart(message.parts[item.index], item.index)}
            </Fragment>
          ))}
        />
        {renderDividers(dividers)}
      </Fragment>
    );
  };

  const renderCodingPart = () => {
    return codingSegments.map((segment) => {
      if (segment.kind !== "summary") return renderCodingTimelineSegment(segment);

      return (
        <CodingTurnSummary
          key={`message-${message.id}-${segment.id}`}
          stepCount={segment.stepCount}
          duration={turnDuration(codingMetadata)}
          failed={codingMetadata?.status === "failed"}
        >
          {segment.segments.map(renderCodingTimelineSegment)}
        </CodingTurnSummary>
      );
    });
  };

  const renderMessagePart = () => {
    if (isCodingSurface) return renderCodingPart();

    return activitySegments.map((segment) => {
      if (segment.kind === "part") {
        const part = message.parts[segment.index];
        const element = renderOnePart(part, segment.index);
        const dividers = dividersForPart(part);
        if (dividers.length === 0) return element;
        return (
          <Fragment key={`message-${message.id}-part-${segment.index}-with-compaction`}>
            {element}
            {renderDividers(dividers)}
          </Fragment>
        );
      }

      // Compaction dividers stay on the timeline rather than being collapsed
      // away with the steps they were anchored to.
      const dividers = segment.items.flatMap((item) =>
        dividersForPart(message.parts[item.index]),
      );

      return (
        <Fragment key={`message-${message.id}-${segment.id}`}>
          <MessageActivityGroup
            label={segment.label}
            stepCount={segment.stepCount}
            isStreaming={segment.isStreaming}
            currentLabel={segment.currentLabel}
          >
            {segment.items.map((item) => (
              <MessageActivityStep
                key={`message-${message.id}-part-${item.index}-step`}
                duplicateCount={item.duplicateCount}
              >
                {renderOnePart(message.parts[item.index], item.index)}
              </MessageActivityStep>
            ))}
          </MessageActivityGroup>
          {renderDividers(dividers)}
        </Fragment>
      );
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid={`message-${message.role}`}
        className={cn("group/message mx-auto w-full", isUserMessage ?"mb-3": "",
          isCompact ? "max-w-full px-2" : "max-w-full px-3 lg:max-w-3xl xl:max-w-4xl lg:px-4")
        }
        initial={{ y: 5, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        data-role={message.role}
      >
        <div
          className={cn(
            "flex w-full min-w-0",
            isCompact ? "gap-2" : "gap-4",
            "group-data-[role=user]/message:ml-auto",
            isCompact ? "group-data-[role=user]/message:max-w-full" : "group-data-[role=user]/message:max-w-2xl",
            {
              "w-full": mode === "edit",
              "group-data-[role=user]/message:w-fit":
                mode !== "edit" && !isFormSubmissionMessage,
              // A receipt spans the column instead of shrink-wrapping.
              "group-data-[role=user]/message:w-full group-data-[role=user]/message:max-w-full":
                isFormSubmissionMessage,
            }
          )}
        >
          <div className={cn("w-full min-w-0", isCompact ? "gap-2" : "gap-4")}>
            {fileParts.length > 0 && (
              <div
                data-testid="message-files"
                className={cn("flex flex-row flex-wrap justify-end mb-2", isCompact ? "gap-1" : "gap-2")}
              >
                {fileParts.map((filePart) => (
                  <PreviewAttachment
                    key={filePart.url}
                    attachment={{
                      url: filePart.url,
                      name: (filePart as any).name ?? "",
                      contentType: filePart.mediaType,
                      size: 0, // Size not available from filePart
                      type: "file" as const, // Default type
                    }}
                  />
                ))}
              </div>
            )}

            {/**
             * Handle error messages with proper UI. The error message for that particular
             * message will be added to the metadata object. So we here take it from the
             * metadata and add to the UI.
             */}
            {hasError && (
              <ErrorMessage
                timestamp={message.metadata?.createdAt}
                message={message.metadata?.errorMessage}
                onRetry={!isReadonly ? () => regenerate({ messageId: message.id }) : undefined}
              />
            )}

            {/**
             * We show the actual message parts only when there is no error. Even of there is an error
             * we will have the message parts available for the user to see. No need to show this if the status
             * is error for this message
             */}
            {!hasError && renderMessagePart()}

            {/**
             * What the coding turn changed, under its answer. The per-file steps
             * are behind the collapsed summary by then, so this is the only
             * place a finished turn states its result.
             */}
            {!hasError && codingChanges !== undefined && (
              <CodingFileSummary summary={codingChanges} />
            )}

            {/**
             * Auto-rendered chip list for files the agent wrote but never
             * wrapped in a chip-emitting tool call. Skipped entirely when any
             * other chip surface (complete / finish_and_respond / render_ui)
             * or an artifact tool was invoked in this message.
             */}
            {!hasError && shouldRenderAutoChips && autoChipFiles && (
              <div className="mt-3">
                <DetailedAttachmentList
                  attachments={visibleChipFiles}
                  sandboxId={sandboxId}
                />
                {hiddenChipCount > 0 && (
                  <div className="mt-1.5 text-xs text-muted-foreground">
                    {canOpenLibraryTab ? (
                      <button
                        type="button"
                        onClick={openLibraryTab}
                        className="underline underline-offset-2 transition-colors hover:text-foreground"
                      >
                        {hiddenChipCount} more {hiddenChipCount === 1 ? "file" : "files"} in the Library
                      </button>
                    ) : (
                      <span>
                        {hiddenChipCount} more {hiddenChipCount === 1 ? "file" : "files"} in the Library
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/**
             * Render message actions for assistant messages.
             * Visibility is driven purely by CSS (group-hover/focus-within) so React
             * state changes don't re-render the whole subtree on every mouse move,
             * which is what was causing flicker. Space is reserved (h-8) so layout
             * doesn't shift when actions fade in.
             */}
            {canShowActions && (
              <div
                className={cn(
                  "h-8 transition-opacity opacity-0 group-hover/message:opacity-100 focus-within:opacity-100",
                  isSpeaking && "opacity-100"
                )}
              >
                <MessageActions
                  key={`action-${message.id}`}
                  message={message}
                  isLoading={isLoading}
                  role={message.role}
                  isReadonly={isReadonly}
                  regenerate={() => regenerate({ messageId: message.id })}
                  onEdit={() => setMode("edit")}
                  textToSpeechEnabled={textToSpeechEnabled}
                  isSpeaking={isSpeaking}
                  isTTSSupported={isTTSSupported}
                  onSpeak={() => speak(getMessageContent())}
                  onStopSpeaking={stop}
                />
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * PreviewMessage is a memoized version of PurePreviewMessage for performance optimization.
 *
 * This component uses React.memo to prevent unnecessary re-renders by implementing a custom
 * comparison function that checks for specific prop changes. The memoization is crucial for
 * chat performance because:
 *
 * 1. **Performance Optimization**: Chat messages are frequently re-rendered as new messages
 *    arrive, users scroll, or the chat state changes. Without memoization, every message
 *    would re-render on each state update, causing performance issues in long conversations.
 *
 * 2. **Smart Re-rendering**: The custom comparison function only triggers re-renders when
 *    meaningful changes occur:
 *    - Loading state changes (isLoading)
 *    - Message ID changes (different message)
 *    - Scroll padding requirements change
 *    - Message content changes (parts array)
 *
 * 3. **Deep Comparison**: Uses fast-deep-equal for comparing complex objects like message
 *    parts to ensure accurate change detection without reference equality issues.
 *
 * 4. **Animation Stability**: Prevents animation glitches that can occur when components
 *    re-render unnecessarily during chat interactions.
 *
 * The memo function returns `false` for most comparisons, meaning it will re-render unless
 * the props are truly unchanged, ensuring the UI stays responsive while optimizing performance.
 *
 * @param props - Same props as PurePreviewMessage
 * @returns Memoized JSX.Element that only re-renders when necessary
 */
export const PreviewMessage = memo(
  PurePreviewMessage,
  (prevProps, nextProps) => {
    // Always re-render during streaming to show live content updates
    // isLoading is true for the last message while status === "streaming"
    if (nextProps.isLoading) {
      return false;
    }
    if (prevProps.isLoading !== nextProps.isLoading) {
      return false;
    }
    if (prevProps.message.id !== nextProps.message.id) {
      return false;
    }
    if (prevProps.requiresScrollPadding !== nextProps.requiresScrollPadding) {
      return false;
    }
    if (!equal(prevProps.message.parts, nextProps.message.parts)) {
      return false;
    }
    if (!equal(prevProps.compactions, nextProps.compactions)) {
      return false;
    }
    if (prevProps.textToSpeechEnabled !== nextProps.textToSpeechEnabled) {
      return false;
    }
    if (!equal(prevProps.voiceSettings, nextProps.voiceSettings)) {
      return false;
    }
    if (prevProps.nextUserMessageText !== nextProps.nextUserMessageText) {
      return false;
    }
    if (!equal(prevProps.agentStreams, nextProps.agentStreams)) {
      return false;
    }
    if (!equal(prevProps.agentStreamStats, nextProps.agentStreamStats)) {
      return false;
    }
    if (!equal(prevProps.agentFinalTexts, nextProps.agentFinalTexts)) {
      return false;
    }
    // Re-render when the artifact render plan flips (preview ↔ badge ↔ hidden)
    // so superseded creates collapse without waiting for an unrelated change.
    if (!equal(prevProps.artifactRenderPlan, nextProps.artifactRenderPlan)) {
      return false;
    }
    // Re-render when the per-turn auto-chip plan changes — keeps the chip
    // surface in sync as new file writes arrive within the same turn.
    if (!equal(prevProps.autoChipFiles, nextProps.autoChipFiles)) {
      return false;
    }
    // Re-render when sandboxId becomes available — the first message in a
    // brand-new chat renders before the sandboxSession is wired up, so the
    // chip surface receives sandboxId=undefined initially. Without this check
    // the chip never sees the resolved id and preview/download silently fail.
    if (prevProps.sandboxId !== nextProps.sandboxId) {
      return false;
    }
    // Re-render when the message stops being the final assistant of its turn
    // (e.g. a new turn arrives) so the action footer collapses on demoted
    // intermediates without waiting for an unrelated change.
    if (prevProps.isLastAssistantOfTurn !== nextProps.isLastAssistantOfTurn) {
      return false;
    }
    // Props are equal, skip re-render
    return true;
  }
);
